// The real per-SKU manifest for a lot isn't stored (lots sell sealed), so we
// synthesize a stable, plausible line-item breakdown from the listing. Seeded
// off the listing id so the same lot always yields the same manifest, and the
// same row index resolves to the same SKU on the standalone detail page.

const VARIANTS = [
  "Turtledove/True White", "True White", "Tan", "Black/White",
  "Check Camo Obsidian/Fern", "Checkerboard Black/True White", "Obsidian/Sail",
  "Grey/Gum", "Navy/White", "Triple Black", "Cream/Sail", "Red/True White",
  "Fog/White", "Sage/Bone", "Chocolate/Portobello", "Ice Blue/White",
];
const DEPTS = ["kids", "mens", "womens", "unisex"];
const SIZES = ["2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12", "13", "XS", "S", "M", "L", "XL"];
const CONDITIONS = ["Brand new, sealed", "New with box", "New, factory case"];
const IP_RISKS = ["N/A", "Low", "Cleared", "Monitor"];

export type ManifestRow = {
  variant: string;
  dept: string;
  size: string;
  upc: string;
  units: number;
  sku: string;
  condition: string;
  ipRisk: string;
  fba: boolean;
};

function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return h >>> 0;
}
function rng(seed: number): () => number {
  let t = seed;
  return () => {
    t += 0x6d2b79f5;
    let x = Math.imul(t ^ (t >>> 15), t | 1);
    x ^= x + Math.imul(x ^ (x >>> 7), x | 61);
    return ((x ^ (x >>> 14)) >>> 0) / 4294967296;
  };
}
function upc(seed: number): string {
  const r = rng(seed);
  let s = "";
  for (let i = 0; i < 12; i++) s += Math.floor(r() * 10);
  return s;
}

export function buildManifest(listingId: string, units: number): ManifestRow[] {
  const r = rng(hash(listingId));
  const out: ManifestRow[] = [];
  let remaining = Math.max(units, 1);
  let i = 0;
  while (remaining > 0 && out.length < 400) {
    const take = Math.min(remaining, 1 + Math.floor(r() * 9));
    out.push({
      variant: VARIANTS[Math.floor(r() * VARIANTS.length)],
      dept: DEPTS[Math.floor(r() * DEPTS.length)],
      size: SIZES[Math.floor(r() * SIZES.length)],
      upc: upc(hash(listingId) + i * 7919),
      units: take,
      sku: `DD${1000 + Math.floor(r() * 9000)}-${100 + Math.floor(r() * 899)}`,
      condition: CONDITIONS[Math.floor(r() * CONDITIONS.length)],
      ipRisk: IP_RISKS[Math.floor(r() * IP_RISKS.length)],
      fba: r() > 0.5,
    });
    remaining -= take;
    i++;
  }
  return out;
}

// The short product name repeated per SKU row (all rows are the same product).
export function manifestProductName(title: string): string {
  return title.split(/\s+/).slice(0, 3).join(" ");
}

// ---- Per-brand pricing breakdown -----------------------------------------
// A mixed wholesale lot spans many brands; the default manifest view rolls the
// lot up by brand. `slug` is the Simple Icons id for the brand mark (undefined
// brands fall back to a lettered badge in the UI).
const BRANDS: { name: string; slug?: string }[] = [
  { name: "Nike", slug: "nike" },
  { name: "Adidas", slug: "adidas" },
  { name: "Reebok", slug: "reebok" },
  { name: "Skechers" },
  { name: "Jordan", slug: "jordan" },
  { name: "Crocs", slug: "crocs" },
  { name: "New Balance", slug: "newbalance" },
  { name: "Puma", slug: "puma" },
  { name: "Under Armour", slug: "underarmour" },
  { name: "Vans", slug: "vans" },
  { name: "Converse", slug: "converse" },
  { name: "Asics", slug: "asics" },
  { name: "Fila", slug: "fila" },
  { name: "Lacoste", slug: "lacoste" },
  { name: "Timberland" },
  { name: "Caterpillar" },
  { name: "Wolverine" },
  { name: "Lee" },
  { name: "Dockers" },
  { name: "Avia" },
];

export type BrandRow = {
  name: string;
  slug?: string;
  units: number;
  avgMsrpCents: number;
  pricePairCents: number;
  pctOff: number; // whole-number percent
  extCostCents: number;
};

export function buildBrandBreakdown(
  listingId: string,
  units: number,
  msrpCents: number,
): BrandRow[] {
  const base = msrpCents > 0 ? msrpCents : 7500;
  const r = rng(hash(listingId + ":brand"));
  const pool = [...BRANDS];
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(r() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  const count = Math.min(pool.length, 8 + Math.floor(r() * 8)); // 8-15 brands
  const chosen = pool.slice(0, count);
  const weights = chosen.map(() => Math.pow(r(), 1.8) + 0.02);
  const wsum = weights.reduce((a, b) => a + b, 0);

  let allocated = 0;
  const rows: BrandRow[] = chosen.map((b, i) => {
    const u = Math.max(1, Math.round((units * weights[i]) / wsum));
    allocated += u;
    const avgMsrpCents = Math.max(500, Math.round(base * (0.6 + r() * 1.1)));
    const off = 0.55 + r() * 0.34; // 55%-89% off
    const pricePairCents = Math.max(100, Math.round(avgMsrpCents * (1 - off)));
    return {
      name: b.name,
      slug: b.slug,
      units: u,
      avgMsrpCents,
      pricePairCents,
      pctOff: Math.round(off * 100),
      extCostCents: 0,
    };
  });

  rows.sort((a, b) => b.units - a.units);
  // Reconcile rounding onto the largest brand so units sum exactly to the lot.
  if (rows.length) rows[0].units = Math.max(1, rows[0].units + (units - allocated));
  rows.forEach((row) => (row.extCostCents = row.pricePairCents * row.units));
  return rows;
}
